import React, { useState } from 'react';
import { Plus, Trash2, Edit2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';

interface GenericListManagerProps {
  items: string[];
  onChange: (items: string[]) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  emptyMessage?: string;
  addButtonText?: string;
  singularLabel?: string;
  pluralLabel?: string;
  minHeight?: string;
  showNumbers?: boolean;
}

export function GenericListManager({ 
  items, 
  onChange, 
  disabled = false,
  label = 'Items',
  placeholder = 'Escribe aquí...',
  emptyMessage = 'No hay items agregados aún',
  addButtonText = 'Agregar nuevo item',
  singularLabel = 'item',
  pluralLabel = 'items',
  minHeight = '80px',
  showNumbers = true
}: GenericListManagerProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [newItem, setNewItem] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleAdd = () => {
    if (newItem.trim()) {
      onChange([...items, newItem.trim()]);
      setNewItem('');
      setIsAdding(false);
    }
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setEditText(items[index]);
  };

  const handleSaveEdit = () => {
    if (editText.trim() && editingIndex !== null) {
      const updated = [...items];
      updated[editingIndex] = editText.trim();
      onChange(updated);
      setEditingIndex(null);
      setEditText('');
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditText('');
  };

  const handleDelete = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      action();
    }
  };

  return (
    <div className="space-y-3 w-full overflow-hidden">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <span className="text-xs text-gray-500">
          {items.length} {items.length === 1 ? singularLabel : pluralLabel}
        </span>
      </div>

      {/* Lista de items existentes */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {items.map((item, index) => (
          <Card key={index} className="border border-gray-200 shadow-sm overflow-hidden">
            <CardContent className="p-3">
              {editingIndex === index ? (
                // Modo edición
                <div className="space-y-2">
                  <Textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => handleKeyPress(e, handleSaveEdit)}
                    className="resize-y w-full"
                    style={{ minHeight }}
                    autoFocus
                  />
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleCancelEdit}
                      className="gap-1"
                    >
                      <X className="h-3 w-3" />
                      <span className="hidden sm:inline">Cancelar</span>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSaveEdit}
                      className="gap-1 bg-green-600 hover:bg-green-700"
                    >
                      <Check className="h-3 w-3" />
                      <span className="hidden sm:inline">Guardar</span>
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 hidden sm:block">Tip: Ctrl + Enter para guardar rápido</p>
                </div>
              ) : (
                // Modo vista
                <div className="flex gap-2 items-start overflow-hidden">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-start gap-2">
                      {showNumbers && (
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-1 shrink-0">
                          #{index + 1}
                        </span>
                      )}
                      <p className="text-sm text-gray-600 dark:text-gray-400 flex-1 whitespace-pre-wrap wrap-break-word overflow-wrap-anywhere">
                        {item}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(index)}
                      disabled={disabled}
                      className="h-8 w-8 p-0"
                      title="Editar"
                    >
                      <Edit2 className="h-4 w-4 text-blue-600" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(index)}
                      disabled={disabled}
                      className="h-8 w-8 p-0"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {items.length === 0 && !isAdding && (
          <div className="text-center py-8 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg">
            {emptyMessage}
          </div>
        )}
      </div>

      {/* Formulario para agregar nuevo item */}
      {isAdding ? (
        <Card className="border border-blue-200 bg-blue-50/50 dark:bg-gray-950 overflow-hidden">
          <CardContent className="p-3 space-y-2">
            <Label className="text-sm">{singularLabel}</Label>
            <Textarea
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => handleKeyPress(e, handleAdd)}
              placeholder={placeholder}
              className="resize-y bg-white w-full"
              style={{ minHeight: '100px' }}
              autoFocus
            />
            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsAdding(false);
                  setNewItem('');
                }}
              >
                <X className="h-3 w-3 sm:mr-1" />
                <span className="hidden sm:inline">Cancelar</span>
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleAdd}
                disabled={!newItem.trim()}
                className="bg-blue-500 hover:bg-blue-600"
              >
                <Check className="h-3 w-3 sm:mr-1" />
                <span className="hidden sm:inline">Agregar</span>
              </Button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-300 hidden sm:block">Tip: Ctrl + Enter para agregar rápido</p>
          </CardContent>
        </Card>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsAdding(true)}
          disabled={disabled}
          className="w-full border-dashed border-2 hover:border-blue-400 hover:bg-blue-50"
        >
          <Plus className="h-4 w-4 mr-2" />
          {addButtonText}
        </Button>
      )}
    </div>
  );
}

// Demo component mostrando diferentes casos de uso
export default function Demo() {
  const [functions, setFunctions] = useState([
    'Supervisar y coordinar las actividades del equipo de desarrollo de software.',
    'Realizar revisiones de código y asegurar las mejores prácticas.',
    'Gestionar proyectos utilizando metodologías ágiles (Scrum/Kanban).',
  ]);

  const [responsibilities, setResponsibilities] = useState([
    'Mantener la documentación técnica actualizada',
    'Participar en reuniones de planificación',
  ]);

  const [goals, setGoals] = useState([
    'Aumentar la productividad del equipo en un 20%',
  ]);

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8 space-y-12">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Generic List Manager</h1>
        <p className="text-gray-600 text-sm sm:text-base">Componente reutilizable para gestionar listas de texto</p>
      </div>

      {/* Ejemplo 1: Funciones clave */}
      <div className="bg-white p-4 sm:p-6 rounded-xl border shadow-sm">
        <h2 className="text-lg sm:text-xl font-semibold mb-4 text-blue-600">Ejemplo 1: Funciones Clave</h2>
        <GenericListManager 
          items={functions}
          onChange={setFunctions}
          label="Funciones clave"
          placeholder="Describe la función clave del colaborador..."
          emptyMessage="No hay funciones agregadas aún"
          addButtonText="Agregar nueva función"
          singularLabel="función"
          pluralLabel="funciones"
        />
        <div className="mt-4 p-3 bg-gray-50 rounded text-xs overflow-auto">
          <strong>Datos:</strong> {JSON.stringify(functions, null, 2)}
        </div>
      </div>

      {/* Ejemplo 2: Responsabilidades */}
      <div className="bg-white p-4 sm:p-6 rounded-xl border shadow-sm">
        <h2 className="text-lg sm:text-xl font-semibold mb-4 text-green-600">Ejemplo 2: Responsabilidades</h2>
        <GenericListManager 
          items={responsibilities}
          onChange={setResponsibilities}
          label="Responsabilidades del puesto"
          placeholder="Describe una responsabilidad..."
          emptyMessage="No hay responsabilidades definidas"
          addButtonText="Agregar responsabilidad"
          singularLabel="responsabilidad"
          pluralLabel="responsabilidades"
          minHeight="60px"
        />
      </div>

      {/* Ejemplo 3: Objetivos */}
      <div className="bg-white p-4 sm:p-6 rounded-xl border shadow-sm">
        <h2 className="text-lg sm:text-xl font-semibold mb-4 text-purple-600">Ejemplo 3: Objetivos Anuales</h2>
        <GenericListManager 
          items={goals}
          onChange={setGoals}
          label="Objetivos del año"
          placeholder="Define un objetivo SMART..."
          emptyMessage="No hay objetivos establecidos"
          addButtonText="Agregar objetivo"
          singularLabel="objetivo"
          pluralLabel="objetivos"
          showNumbers={false}
        />
      </div>

      {/* Guía de uso */}
      <div className="bg-linear-to-r from-blue-50 to-purple-50 p-4 sm:p-6 rounded-xl border">
        <h3 className="font-bold mb-3 text-sm sm:text-base">📖 Cómo usar el componente:</h3>
        <div className="text-xs sm:text-sm space-y-2 text-gray-700">
          <p><strong>Props principales:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><code className="bg-white px-2 py-0.5 rounded">items</code>: Array de strings con los datos</li>
            <li><code className="bg-white px-2 py-0.5 rounded">onChange</code>: Función para actualizar los datos</li>
            <li><code className="bg-white px-2 py-0.5 rounded">label</code>: Título del campo</li>
            <li><code className="bg-white px-2 py-0.5 rounded">placeholder</code>: Texto de ayuda</li>
            <li><code className="bg-white px-2 py-0.5 rounded">singularLabel / pluralLabel</code>: Para el contador</li>
            <li><code className="bg-white px-2 py-0.5 rounded">showNumbers</code>: Mostrar/ocultar numeración</li>
          </ul>
        </div>
      </div>
    </div>
  );
}